import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/http/client';

interface EntitlementScopesResponse {
  scopes: string[];
}

export interface EntitlementScopesResult {
  scopes: string[];
  isLoading: boolean;
}

/**
 * Hook to fetch entitlement scopes for an organization.
 *
 * Uses React Query for caching, deduplication, and automatic refetching.
 * Scopes are cached for 5 minutes per org.
 *
 * @param orgId - Organization ID
 * @returns Object with scopes array and isLoading flag
 */
export function useEntitlementScopes(
  orgId: number | undefined
): EntitlementScopesResult {
  const { data, isLoading } = useQuery({
    queryKey: ['entitlementScopes', orgId],
    queryFn: async () => {
      const res = await apiGet<EntitlementScopesResponse>(
        `/api/orgs/${orgId}/entitlement-scopes/`
      );
      return res.scopes ?? [];
    },
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });

  return { scopes: data ?? [], isLoading };
}
